import React from "react";

import { addDatadogError } from "../../services/datadog";
import "./LazyLoadBoundary.scss";

interface ILazyLoadBoundaryProps {
	/** Owned by the caller: an error boundary must not depend on hooks or providers. */
	message: string;
	retryLabel: string;
	children: React.ReactNode;
}

interface ILazyLoadBoundaryState {
	hasError: boolean;
}

/**
 * A lazy chunk that fails to load throws during render, and with nothing to catch it
 * React unmounts the whole app — a blank page on the payment step. The routine causes
 * are a redeploy invalidating the hashed chunk under an open tab, or the connection
 * dropping for that one request; a reload picks up the new hashes and recovers both.
 */
class LazyLoadBoundary extends React.Component<ILazyLoadBoundaryProps, ILazyLoadBoundaryState> {
	constructor(props: ILazyLoadBoundaryProps) {
		super(props);
		this.state = { hasError: false };
	}

	static getDerivedStateFromError(): ILazyLoadBoundaryState {
		return { hasError: true };
	}

	componentDidCatch(error: Error): void {
		addDatadogError(error);
	}

	handleReload = (): void => {
		globalThis.location.reload();
	};

	render(): React.ReactNode {
		const { message, retryLabel, children } = this.props;

		if (!this.state.hasError) {
			return children;
		}

		return (
			<div className="LazyLoadBoundary" data-testid="LazyLoadBoundary">
				<p>{message}</p>
				<button type="button" className="LazyLoadBoundaryReload" onClick={this.handleReload}>
					{retryLabel}
				</button>
			</div>
		);
	}
}

export default LazyLoadBoundary;
